import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loginWithPassword } from "@/lib/auth";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, AlertCircle } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const result = await loginWithPassword(email, password);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    window.location.href = result.user.mustChangePassword ? "/change-password" : "/";
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3 items-center text-center">
          <img src="/aquila-logo.svg" alt="Aquila" className="h-12 w-auto" />
          <CardTitle className="text-lg">Reporting Engine sign-in</CardTitle>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Authorised users only</p>
        </CardHeader>
        <CardContent>
          <form className="space-y-3" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={busy}
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={busy}
                data-testid="login-password"
              />
            </div>
            {error && (
              <div className="rounded-md bg-red-50 border border-red-300/60 dark:bg-red-950/40 dark:border-red-700/40 px-3 py-2 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">
              <Lock className="h-4 w-4 mr-2" />
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="mt-4 text-xs text-muted-foreground text-center">
            Lost your password? Ask Jon to reset it.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
